if (!customElements.get('frame-picker')) {
  customElements.define('frame-picker', class FramePicker extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', this.onVariantChange.bind(this));
      this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.updateOptions();
      
      // Sync initial state on load
      this.isUpdating = true;
      this.selectedInputs.forEach((input) => {
        const optionIndex = parseInt(input.dataset.optionIndex);
        const optionValue = input.value;
        const optionName = input.dataset.optionName || '';
        this.syncNativeSelectors(optionIndex, optionValue, optionName);
      });
      this.isUpdating = false;

      this.updateMasterId();
      this.updateMasterIdInForm();
    }

    onVariantChange(event) {
      // Prevent infinite loops from programmatic changes
      if (this.isUpdating) return;
      
      // Only react to changes on actual selection inputs (radios, selects, hiddens)
      if (event && event.target && !event.target.classList.contains('frame-picker__input')) {
        return;
      }

      this.updateOptions();
      const previousVariantId = this.currentVariant ? this.currentVariant.id : null;
      
      // Pass the triggering input to find the best match
      this.updateMasterId(event ? event.target : null);

      // Only proceed if the variant has actually changed
      if (this.currentVariant && this.currentVariant.id !== previousVariantId) {
        if (this.dataset.forceReload === 'true') {
          this.forceReload();
          return;
        }

        // Sync selections back to theme's native option selectors
        this.isUpdating = true;
        this.selectedInputs.forEach((input) => {
          const optionIndex = parseInt(input.dataset.optionIndex);
          const optionValue = input.value;
          const optionName = input.dataset.optionName || '';
          this.syncNativeSelectors(optionIndex, optionValue, optionName);
        });
        this.isUpdating = false;

        this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo();
        this.updateSelf();
      }
    }

    updateOptions() {
      // Find all inputs that belong to this section
      const sectionId = this.dataset.section;
      const inputs = Array.from(this.querySelectorAll('.frame-picker__input'));
      
      this.selectedInputs = inputs.filter(input => {
        if (input.tagName === 'SELECT') return true;
        if (input.type === 'radio') return input.checked;
        if (input.type === 'hidden') return true;
        return false;
      });
    }

    updateMasterId(triggerInput) {
      // 1. Try exact match first
      let match = this.variants.find((variant) => {
        return this.selectedInputs.every((input) => {
          const index = parseInt(input.dataset.optionIndex);
          const optionValue = input.value.trim();
          const variantValue = variant.options[index] ? variant.options[index].trim() : '';
          return variantValue === optionValue;
        });
      });

      // 2. Fallback: Find closest available variant
      if (!match && triggerInput) {
        const triggerIndex = parseInt(triggerInput.dataset.optionIndex);
        const triggerValue = triggerInput.value.trim();

        // Filter variants that match the option user just clicked
        const potentialVariants = this.variants.filter(variant => 
          variant.options[triggerIndex] && variant.options[triggerIndex].trim() === triggerValue
        );

        if (potentialVariants.length > 0) {
          // Sort by how many other options they match
          match = potentialVariants.sort((a, b) => {
            let aMatches = 0;
            let bMatches = 0;

            this.selectedInputs.forEach(input => {
              const idx = parseInt(input.dataset.optionIndex);
              if (idx === triggerIndex) return;
              
              const val = input.value.trim();
              if (a.options[idx] && a.options[idx].trim() === val) aMatches++;
              if (b.options[idx] && b.options[idx].trim() === val) bMatches++;
            });

            return bMatches - aMatches;
          })[0];
        }
      }

      this.currentVariant = match || this.variants[0];
    }

    updateMasterIdInForm() {
      if (this.currentVariant) {
        const variantIdInputs = document.querySelectorAll('[name="id"]');
        variantIdInputs.forEach((input) => {
          if (input.classList.contains('frame-picker__variant-id') || input.closest('form')) {
            input.value = this.currentVariant.id;
          }
        });
      }
    }

    forceReload() {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.location.href = url.href;
    }

    syncNativeSelectors(optionIndex, optionValue, optionName) {
      if (!optionName) return;
      
      const optionNameEscaped = optionName.replace(/"/g, '\\"');
      
      // Selectors representing common theme option elements
      const selectors = [
        `[name="options[${optionNameEscaped}]"]`,
        `[name="options[${optionNameEscaped.toLowerCase()}]"]`,
        `[name="${optionNameEscaped}"]`,
        `[name="${optionNameEscaped.toLowerCase()}"]`,
        `[name="option-${optionIndex}"]`,
        `[name="option${optionIndex + 1}"]`,
        `[data-option-index="${optionIndex}"]`,
        `[data-index="option${optionIndex + 1}"]`,
        `#SingleOptionSelector-${optionIndex}`,
        `.single-option-selector-${optionIndex}`,
        `[id*="SingleOptionSelector"][id*="-${optionIndex}"]`,
        `[class*="single-option-selector"][class*="-${optionIndex}"]`
      ];

      selectors.forEach(selectorString => {
        const elements = document.querySelectorAll(selectorString);
        elements.forEach(element => {
          // Prevent syncing with elements inside our own block
          if (this.contains(element)) return;

          let valueChanged = false;

          if (element.tagName === 'SELECT') {
            const optionToSelect = Array.from(element.options).find(opt => 
              opt.value.trim().toLowerCase() === optionValue.trim().toLowerCase()
            );
            if (optionToSelect && element.value !== optionToSelect.value) {
              element.value = optionToSelect.value;
              valueChanged = true;
            }
          } else if (element.tagName === 'INPUT' && (element.type === 'radio' || element.type === 'checkbox')) {
            if (element.value.trim().toLowerCase() === optionValue.trim().toLowerCase()) {
              if (!element.checked) {
                element.checked = true;
                valueChanged = true;
              }
            } else {
              if (element.checked) {
                element.checked = false;
              }
            }
          }

          if (valueChanged) {
            // Trigger standard browser events
            element.dispatchEvent(new Event('change', { bubbles: true }));
            element.dispatchEvent(new Event('input', { bubbles: true }));

            // Trigger jQuery events for themes utilizing jQuery (older Testament / custom themes)
            if (window.jQuery) {
              window.jQuery(element).trigger('change');
            }
          }
        });
      });
    }

    updateVariantInput() {
      this.isUpdating = true;
      
      if (!this.currentVariant) return;

      // Find all possible variant ID inputs on the page (main form, sticky, mobile, etc.)
      const variantIdInputs = document.querySelectorAll('[name="id"]');
      
      variantIdInputs.forEach((input) => {
        const form = input.closest('form');
        
        // Check if this input belongs to a product form
        const isProductForm = form && (
          form.action.includes('/cart/add') || 
          form.dataset.type === 'add-to-cart-form' ||
          form.classList.contains('product-form') ||
          form.querySelector('[name="add"]')
        );

        // Update the value if it's a product form or our own internal input
        if (isProductForm || input.classList.contains('frame-picker__variant-id')) {
          input.value = this.currentVariant.id;
          
          // 1. Native DOM events
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.dispatchEvent(new Event('input', { bubbles: true }));

          // 2. jQuery events
          if (window.jQuery) {
            window.jQuery(input).trigger('change');
          }
          
          // 3. Form-level events
          if (form) {
            form.dispatchEvent(new Event('change', { bubbles: true }));
            form.dispatchEvent(new CustomEvent('variant-change', {
              bubbles: true,
              detail: {
                variant: this.currentVariant,
                sectionId: this.dataset.section
              }
            }));
          }
        }
      });

      // 4. Global document events
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: this.currentVariant,
          product: this.dataset.productId,
          sectionId: this.dataset.section
        }
      }));

      // Release the guard after events have had a chance to bubble
      setTimeout(() => { this.isUpdating = false; }, 100);
    }

    updateURL() {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({ }, '', url.href);
    }

    updateSelf() {
      if (!this.currentVariant) return;

      const sectionId = this.dataset.section;
      const url = `${window.location.pathname}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          
          // Shopify Section Rendering API returns the section HTML.
          // We need to find the frame-picker element inside it.
          const source = html.querySelector('frame-picker');
          const destination = this;

          if (source && destination) {
            // Replace the entire inner content of the component
            // This ensures custom liquid blocks, prices, and images are all updated
            destination.innerHTML = source.innerHTML;
            
            // Sync the internal variant ID input which might have been replaced
            const internalInput = destination.querySelector('.frame-picker__variant-id');
            if (internalInput) internalInput.value = this.currentVariant.id;

            // Re-run updateOptions to pick up new inputs
            this.updateOptions();
            
            // Dispatch a final event to signal UI update is complete
            this.dispatchEvent(new CustomEvent('frame-picker:updated', {
              bubbles: true,
              detail: { variant: this.currentVariant }
            }));
          }
        })
        .catch((e) => {
          console.error('Frame Picker: Error updating container', e);
        });
    }

    renderProductInfo() {
      this.dispatchEvent(new CustomEvent('variant-change', {
        bubbles: true,
        detail: {
          variant: this.currentVariant,
          sectionId: this.dataset.section
        }
      }));
    }
  });
}
